import { useEffect, useState } from 'react';
import {
    Box,
    HStack,
    Icon,
    Modal,
    ModalContent,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import type { BulkActionBarItem } from './BulkActionBar';
import { useBulkActionsShortcut } from '../contexts/BulkActionsShortcutContext';

const PARTBAY_BLUE = '#0071E3';

const menuItemColors = (action: BulkActionBarItem) => {
    const color = action.danger ? 'red.600' : action.success ? 'green.600' : 'gray.700';
    const hoverBg = action.danger ? 'red.50' : action.success ? 'green.50' : '#E6F1FD';
    const hoverColor = action.danger ? 'red.700' : action.success ? 'green.700' : PARTBAY_BLUE;
    const iconColor = action.danger ? 'red.500' : action.success ? 'green.500' : 'gray.500';
    return { color, hoverBg, hoverColor, iconColor };
};

/**
 * Cmd/Ctrl+K dropdown of the active page's bulk actions (no search).
 * Same action list as the floating BulkActionBar.
 */
const BulkActionsShortcutMenu = () => {
    const { session, isMenuOpen, closeMenu } = useBulkActionsShortcut();
    const [highlightIndex, setHighlightIndex] = useState(0);

    const actions = session?.actions ?? [];
    const selectedCount = session?.selectedCount ?? 0;
    const isLoading = session?.isLoading ?? false;

    const actionKey = actions.map((a) => a.id).join('|');

    useEffect(() => {
        if (isMenuOpen) setHighlightIndex(0);
    }, [isMenuOpen, actionKey]);

    const runHighlighted = () => {
        const action = actions[highlightIndex];
        if (!action || isLoading) return;
        session?.onAction(action.id);
        closeMenu();
    };

    if (!session || selectedCount <= 0) return null;

    return (
        <Modal isOpen={isMenuOpen} onClose={closeMenu} isCentered size="sm">
            <ModalOverlay bg="blackAlpha.400" />
            <ModalContent
                borderRadius="xl"
                overflow="hidden"
                boxShadow="none"
                my={0}
                maxW="320px"
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        if (actions.length === 0) return;
                        setHighlightIndex((i) => (i + 1) % actions.length);
                    } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        if (actions.length === 0) return;
                        setHighlightIndex((i) => (i - 1 + actions.length) % actions.length);
                    } else if (e.key === 'Enter') {
                        e.preventDefault();
                        runHighlighted();
                    }
                }}
            >
                <Box px={4} py={3}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {selectedCount} selected
                    </Text>
                </Box>
                <VStack align="stretch" spacing={0} py={1}>
                    {actions.length === 0 ? (
                        <Text px={4} py={3} fontSize="sm" color="gray.500">
                            No available actions
                        </Text>
                    ) : (
                        actions.map((action, index) => {
                            const { color, hoverBg, hoverColor, iconColor } = menuItemColors(action);
                            const highlighted = index === highlightIndex;
                            return (
                                <Box
                                    key={action.id}
                                    as="button"
                                    type="button"
                                    textAlign="left"
                                    w="100%"
                                    px={4}
                                    py={2.5}
                                    bg={highlighted ? hoverBg : 'transparent'}
                                    color={highlighted ? hoverColor : color}
                                    cursor={isLoading ? 'not-allowed' : 'pointer'}
                                    opacity={isLoading ? 0.6 : 1}
                                    disabled={isLoading}
                                    _hover={{ bg: hoverBg, color: hoverColor }}
                                    onMouseEnter={() => setHighlightIndex(index)}
                                    onClick={() => {
                                        if (isLoading) return;
                                        session.onAction(action.id);
                                        closeMenu();
                                    }}
                                >
                                    <HStack spacing={2}>
                                        {action.renderIcon ? (
                                            action.renderIcon()
                                        ) : action.icon ? (
                                            <Icon
                                                as={action.icon}
                                                boxSize={4}
                                                color={highlighted ? hoverColor : iconColor}
                                            />
                                        ) : null}
                                        <Text fontSize="sm" fontWeight="medium">
                                            {action.label}
                                        </Text>
                                    </HStack>
                                </Box>
                            );
                        })
                    )}
                </VStack>
            </ModalContent>
        </Modal>
    );
};

export default BulkActionsShortcutMenu;
